import { FileText, Users, Shield, Heart, CheckCircle, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";
import probateHeroImage from "@/assets/probate-estate-hero.jpg";

const ProbateEstate = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      hero: { title: "Probate & Estate Planning", subtitle: "Protect Your Legacy, Secure Your Family's Future", schedule: "Schedule Consultation", call: "Call" },
      banner: "We understand this is a difficult time. Let us handle the legal complexities with compassion.",
      services: { title: "Our Probate & Estate Services", subtitle: "From estate planning to probate administration, we provide comprehensive legal solutions to protect your family and assets." },
      items: [
        { title: "Probate Administration", description: "Guide you through the probate process efficiently and compassionately." },
        { title: "Estate Planning", description: "Comprehensive planning to protect your assets and loved ones." },
        { title: "Special Needs Trust", description: "Protect assets for loved ones with disabilities while preserving benefits." },
        { title: "Guardianship", description: "Establish legal guardianship for minors or incapacitated adults." }
      ],
      process: { title: "The Probate Process in Texas" },
      steps: [
        { step: "1", title: "File Application", desc: "Application for probate filed with the county court" },
        { step: "2", title: "Court Hearing", desc: "Hearing to approve the will and appoint executor" },
        { step: "3", title: "Inventory & Appraisal", desc: "Document and value all estate assets" },
        { step: "4", title: "Pay Debts & Taxes", desc: "Settle outstanding debts and file tax returns" },
        { step: "5", title: "Distribute Assets", desc: "Distribute remaining assets to beneficiaries" },
        { step: "6", title: "Close Estate", desc: "File final accounting and close the estate" }
      ],
      why: { title: "Why Estate Planning Matters" },
      whyItems: [
        { title: "Protect Your Assets", description: "Ensure your assets go to your chosen beneficiaries, not the state's default rules" },
        { title: "Provide for Loved Ones", description: "Name guardians for minor children and provide for their care" },
        { title: "Avoid Probate Delays", description: "Proper planning can help your family avoid lengthy and costly probate" }
      ]
    },
    es: {
      hero: { title: "Sucesiones y Planificación Patrimonial", subtitle: "Proteja Su Legado, Asegure el Futuro de Su Familia", schedule: "Agendar Consulta", call: "Llamar" },
      banner: "Entendemos que este es un momento difícil. Permítanos manejar las complejidades legales con compasión.",
      services: { title: "Nuestros Servicios de Sucesiones y Patrimonio", subtitle: "Desde planificación patrimonial hasta administración de sucesiones, brindamos soluciones legales integrales para proteger a su familia y activos." },
      items: [
        { title: "Administración de Sucesiones", description: "Lo guiamos a través del proceso de sucesión de manera eficiente y compasiva." },
        { title: "Planificación Patrimonial", description: "Planificación integral para proteger sus activos y seres queridos." },
        { title: "Fideicomiso para Necesidades Especiales", description: "Proteja activos para seres queridos con discapacidades mientras preserva beneficios." },
        { title: "Tutela", description: "Establezca tutela legal para menores o adultos incapacitados." }
      ],
      process: { title: "El Proceso de Sucesión en Texas" },
      steps: [
        { step: "1", title: "Presentar Solicitud", desc: "Solicitud de sucesión presentada ante el tribunal del condado" },
        { step: "2", title: "Audiencia Judicial", desc: "Audiencia para aprobar el testamento y nombrar albacea" },
        { step: "3", title: "Inventario y Avalúo", desc: "Documentar y valorar todos los activos del patrimonio" },
        { step: "4", title: "Pagar Deudas e Impuestos", desc: "Liquidar deudas pendientes y presentar declaraciones de impuestos" },
        { step: "5", title: "Distribuir Activos", desc: "Distribuir activos restantes a beneficiarios" },
        { step: "6", title: "Cerrar Patrimonio", desc: "Presentar contabilidad final y cerrar el patrimonio" }
      ],
      why: { title: "Por Qué Importa la Planificación Patrimonial" },
      whyItems: [
        { title: "Proteja Sus Activos", description: "Asegure que sus activos vayan a sus beneficiarios elegidos, no las reglas predeterminadas del estado" },
        { title: "Provea para Seres Queridos", description: "Nombre tutores para niños menores y provea para su cuidado" },
        { title: "Evite Retrasos de Sucesión", description: "La planificación adecuada puede ayudar a su familia a evitar sucesiones largas y costosas" }
      ]
    },
    vi: {
      hero: { title: "Thừa Kế & Lập Kế Hoạch Di Sản", subtitle: "Bảo Vệ Di Sản Của Bạn, Đảm Bảo Tương Lai Gia Đình", schedule: "Đặt Lịch Tư Vấn", call: "Gọi" },
      banner: "Chúng tôi hiểu đây là thời điểm khó khăn. Hãy để chúng tôi xử lý các phức tạp pháp lý với lòng thương.",
      services: { title: "Dịch Vụ Thừa Kế & Di Sản Của Chúng Tôi", subtitle: "Từ lập kế hoạch di sản đến quản lý thừa kế, chúng tôi cung cấp giải pháp pháp lý toàn diện để bảo vệ gia đình và tài sản của bạn." },
      items: [
        { title: "Quản Lý Thừa Kế", description: "Hướng dẫn bạn qua quy trình thừa kế hiệu quả và thấu cảm." },
        { title: "Lập Kế Hoạch Di Sản", description: "Lập kế hoạch toàn diện để bảo vệ tài sản và người thân của bạn." },
        { title: "Quỹ Tín Thác Nhu Cầu Đặc Biệt", description: "Bảo vệ tài sản cho người thân khuyết tật trong khi bảo tồn quyền lợi." },
        { title: "Quyền Giám Hộ", description: "Thiết lập quyền giám hộ hợp pháp cho trẻ vị thành niên hoặc người lớn mất năng lực." }
      ],
      process: { title: "Quy Trình Thừa Kế tại Texas" },
      steps: [
        { step: "1", title: "Nộp Đơn", desc: "Đơn xin thừa kế nộp cho tòa án quận" },
        { step: "2", title: "Phiên Tòa", desc: "Phiên tòa để phê duyệt di chúc và chỉ định người thi hành" },
        { step: "3", title: "Kiểm Kê & Định Giá", desc: "Lập tài liệu và định giá tất cả tài sản di sản" },
        { step: "4", title: "Trả Nợ & Thuế", desc: "Thanh toán nợ tồn đọng và nộp tờ khai thuế" },
        { step: "5", title: "Phân Phối Tài Sản", desc: "Phân phối tài sản còn lại cho người thụ hưởng" },
        { step: "6", title: "Đóng Di Sản", desc: "Nộp kế toán cuối cùng và đóng di sản" }
      ],
      why: { title: "Tại Sao Lập Kế Hoạch Di Sản Quan Trọng" },
      whyItems: [
        { title: "Bảo Vệ Tài Sản Của Bạn", description: "Đảm bảo tài sản của bạn đến người thụ hưởng bạn chọn, không phải quy tắc mặc định của tiểu bang" },
        { title: "Chu Cấp Cho Người Thân", description: "Chỉ định người giám hộ cho trẻ vị thành niên và chu cấp cho việc chăm sóc của họ" },
        { title: "Tránh Trì Hoãn Thừa Kế", description: "Lập kế hoạch đúng cách có thể giúp gia đình bạn tránh thừa kế dài và tốn kém" }
      ]
    }
  };

  const pageContent = content[language];
  const serviceRoutes = [
    "/practice-areas/probate-estate-planning/probate-administration",
    "/practice-areas/probate-estate-planning/estate-planning",
    "/practice-areas/probate-estate-planning/special-needs-trust",
    "/practice-areas/probate-estate-planning/guardianship"
  ];

  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${probateHeroImage})` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{pageContent.hero.title}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{pageContent.hero.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="cta">
              <LocalizedLink to="/appointment">{pageContent.hero.schedule}</LocalizedLink>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059"><Phone className="mr-2 h-5 w-5" />{pageContent.hero.call}: (832) 930-3059</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-accent/10 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-semibold mb-2"><Heart className="inline h-5 w-5 mr-2 text-accent" />{pageContent.banner}</p>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{pageContent.services.title}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{pageContent.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {pageContent.items.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all border-accent/20 hover:border-accent/50">
                <CardHeader>
                  <div className="text-accent mb-4">
                    {index === 0 ? <FileText className="h-8 w-8" /> : index === 1 ? <Heart className="h-8 w-8" /> : index === 2 ? <Shield className="h-8 w-8" /> : <Users className="h-8 w-8" />}
                  </div>
                  <CardTitle>{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full">
                    <LocalizedLink to={serviceRoutes[index]}>Más Información</LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{pageContent.process.title}</h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {pageContent.steps.map((item) => (
                <div key={item.step} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">{item.step}</div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <QuickContact />

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{pageContent.why.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pageContent.whyItems.map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-accent mb-4 flex justify-center">
                  {index === 0 ? <Shield className="h-12 w-12" /> : index === 1 ? <Users className="h-12 w-12" /> : <FileText className="h-12 w-12" />}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedLinks category="probate" />

      <CTASection />
      <Footer />
    </div>
  );
};

export default ProbateEstate;
